// pages/Documents/ListDocuments.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { Modal as MuiModal, Box } from '@mui/material'; 
import EditDocuments from "./EditDocuments";
import EditDocumentSummary from "./EditDocumentSummary"; // Import the new summary editor
import backendURL from "../../config";
import RetrainButton from "../Dashboard/RetrainButton";


export default function ListDocuments({ refresh }) {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false); // State for summary modal
  const [currentDocument, setCurrentDocument] = useState(null);
  const [triggerFetch, setTriggerFetch] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);

  useEffect(() => {
    document.title = "Uploaded Files | KlonIT Web Application";
    fetchDocuments();
  }, [refresh, triggerFetch]);

  useEffect(() => {
    const filtered = documents.filter((doc) =>
      Object.values(doc)
        .join(" ")
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
    setFilteredDocuments(filtered);
  }, [searchText, documents]);

  const ensureUrlProtocol = (url) => {
    if (!url) return '';
    if (!/^(https?:\/\/|mailto:|tel:)/i.test(url)) {
      return 'https://' + url;
    }
    return url;
  };

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${backendURL}/get-private-data`, { headers });
      const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;

      setDocuments(responseData);
      setFilteredDocuments(responseData);
      setLoading(false);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setError("Authentication error: " + error.response.data.message);
      } else {
        setError("Error fetching documents: " + error.message);
      }
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm(`Are you sure you want to delete document ID ${documentId}? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };

      await axios.delete(`${backendURL}/delete-private-data/${documentId}`, { headers });
      setDocuments(documents.filter((doc) => doc.id !== documentId));
      setFilteredDocuments(filteredDocuments.filter((doc) => doc.id !== documentId));
      setError("Document has been deleted successfully.");
    } catch (error) {
      setError("Error deleting document: " + error.message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments.length === 0) {
      setError("No documents selected for deletion.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedDocuments.length} selected document(s)?`)) return;
    try {
      const token = localStorage.getItem("access_token");
      const headers = { Authorization: `Bearer ${token}` };

      await Promise.all(
        selectedDocuments.map((doc) =>
          axios.delete(`${backendURL}/delete-private-data/${doc.id}`, { headers })
        )
      );

      const remainingDocs = documents.filter(
        (doc) => !selectedDocuments.some((selected) => selected.id === doc.id)
      );
      setDocuments(remainingDocs);
      setFilteredDocuments(remainingDocs);
      setSelectedDocuments([]);
      setToggleCleared(!toggleCleared);
      setError("Selected documents have been deleted successfully.");
    } catch (error) {
      setError("Error deleting documents: " + error.message);
    }
  };

  const handleEdit = (document) => {
    setCurrentDocument(document);
    setIsModalOpen(true);
  };

  const handleEditSummary = (document) => {
    setCurrentDocument(document);
    setIsSummaryModalOpen(true);
  };

  const closeSummaryModal = () => {
    setIsSummaryModalOpen(false);
    setCurrentDocument(null);
  };
  
  const updateFromSummaryModal = () => {
    fetchDocuments();
    closeSummaryModal();
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentDocument(null);
  };
  
  const updateModal = () => {
    fetchDocuments();
    closeModal();
  };

  const columns = [
    { name: "File Name", selector: (row) => row.filename, sortable: true, maxWidth: "200px" },
    { name: "Page Content", selector: (row) => row.page_content, sortable: true, maxWidth: "300px", cell: row => <div style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{row.page_content}</div> },
    { name: "Summary", selector: (row) => row.summary, sortable: true, maxWidth: "300px", cell: row => <div style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{row.summary}</div> },
    { name: "Is Trained?", selector: (row) => (row.is_trained ? "Yes" : "No"), sortable: true, maxWidth: "110px" },
    { name: "Upload Date", selector: (row) => row.upload_date, sortable: true, maxWidth: "150px" },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex">
          <button className="EditIcon" onClick={() => handleEdit(row)} title="Edit Document Content">
            <i className="fa fa-pencil fa-2x"></i>
          </button>
          <button className="EditIcon" onClick={() => handleEditSummary(row)} title="Edit Summary" style={{marginLeft: '10px'}}>
            <i className="fa fa-file-text-o fa-2x"></i>
          </button>
          <button className="deleteIcon" onClick={() => handleDelete(row.id)} title="Delete Document" style={{marginLeft: '10px'}}>
            <i className="fa fa-trash-o fa-2x"></i>
          </button>
        </div> // <<< FIX: This was incorrectly '/>', now it's '</div>'
      ),
      width: '150px',
    },
  ];

  const ExpandedComponent = ({ data }) => (
    <div style={{ padding: '10px', backgroundColor: '#f9f9f9' }}>
      <p><strong>File Name:</strong> {data.filename}</p>
      <p><strong>Summary:</strong> {data.summary || 'N/A'}</p>
      <p><strong>Page Content (HTML):</strong><br/><div dangerouslySetInnerHTML={{ __html: data.page_content }} /></p>
      <p><strong>Document Source:</strong> {data.website_link && data.website_link !== '#' ? <a href={ensureUrlProtocol(data.website_link)} target="_blank" rel="noopener noreferrer">{data.website_link}</a> : 'N/A'}</p>
    </div>
  );

  const muiModalStyle = {
    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: '80%', md: '900px' }, maxWidth: '90vw', maxHeight: '90vh',
    bgcolor: 'background.paper', border: 'none', borderRadius: '8px', boxShadow: 24,
    p: 0,
    display: 'flex', flexDirection: 'column', outline: 'none',
  };

  const muiSummaryModalStyle = { ...muiModalStyle, width: { xs: '90%', sm: '70%', md: '700px' } };

  const handleSelectedRowsChange = ({ selectedRows }) => setSelectedDocuments(selectedRows);

  const contextActions = (<button onClick={handleBulkDelete} className="btn btn-danger">Bulk Delete</button>);

  return (
    <div className="row">
      <div className="col-12 col-lg-12 col-xxl-12 d-flex">
        <div className="card flex-fill">
          <div className="card-header">
            <div className="row">
              <div className="col-lg-4"></div>
              <div className="col-lg-4"><div className="mt-2"><RetrainButton /></div></div>
              <div className="col-lg-4"><input type="text" placeholder="Search documents..." className="form-control mb-3" value={searchText} onChange={(e) => setSearchText(e.target.value)} /></div>
            </div>
          </div>
          <div className="card-body">
            {selectedDocuments.length > 0 && (
              <div className="mt-3">
                <div className="alert alert-info">
                  <div className="row">
                    <div className="col-6">{selectedDocuments.length} rows selected</div>
                    <div className="col-6 text-end"><button onClick={handleBulkDelete} className="btn btn-danger">Bulk Delete</button></div>
                  </div>
                </div>
              </div>
            )}
            {loading ? <p>Loading...</p> : filteredDocuments.length === 0 ? <p>No data available</p> : (
              <DataTable
                columns={columns} data={filteredDocuments} pagination responsive highlightOnHover pointerOnHover
                expandableRows expandableRowsComponent={({ data }) => <ExpandedComponent data={data} />} expandOnRowClicked
                selectableRows onSelectedRowsChange={handleSelectedRowsChange} clearSelectedRows={toggleCleared} contextActions={contextActions}
              />
            )}
            <br />
            {error && <div className="alert alert-warning">{error}</div>}
            <br />
          </div>
        </div>
      </div>
      <MuiModal open={isModalOpen} onClose={closeModal} aria-labelledby="edit-document-modal">
        <Box sx={muiModalStyle}>
          {currentDocument && <EditDocuments document={currentDocument} onClose={closeModal} onUpdate={updateModal} />}
        </Box>
      </MuiModal>

      <MuiModal open={isSummaryModalOpen} onClose={closeSummaryModal} aria-labelledby="edit-summary-modal">
        <Box sx={muiSummaryModalStyle}>
          {currentDocument && <EditDocumentSummary document={currentDocument} onClose={closeSummaryModal} onUpdate={updateFromSummaryModal} />}
        </Box>
      </MuiModal>
    </div>
  );
}